import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../../firebase/config";
import {
  addDoc, collection, deleteDoc, doc, onSnapshot, orderBy, query, setDoc, updateDoc
} from "firebase/firestore";
import { Plus, Save, Trash2 } from "lucide-react";

export default function FAQAdminPage() {
  const [cats, setCats] = useState([]);
  const [catId, setCatId] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    const qCats = query(collection(db, "faq_categories"), orderBy("order", "asc"));
    const unsub = onSnapshot(qCats, (snap) => {
      const arr = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
      setCats(arr);
      if (!catId && arr.length) setCatId(arr[0].id);
    });
    return () => unsub();
  }, [catId]);

  useEffect(() => {
    if (!catId) return;
    const qItems = query(collection(db, "faq_categories", catId, "items"), orderBy("order", "asc"));
    const unsub = onSnapshot(qItems, (snap) => {
      const arr = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
      setItems(arr);
    });
    return () => unsub();
  }, [catId]);

  async function addCategory() {
    const title = prompt("Nombre de la categoría:");
    if (!title) return;
    const order = (cats[cats.length - 1]?.order || 0) + 1;
    await addDoc(collection(db, "faq_categories"), { title, order });
  }
  async function renameCategory(c) {
    const title = prompt("Nuevo nombre:", c.title);
    if (!title || title === c.title) return;
    await updateDoc(doc(db, "faq_categories", c.id), { title });
  }
  async function deleteCategory(c) {
    if (!window.confirm(`Eliminar categoría "${c.title}"? (debes borrar sus items manualmente)`)) return;
    await deleteDoc(doc(db, "faq_categories", c.id));
  }

  async function addItem() {
    const q = prompt("Pregunta:");
    if (!q) return;
    const a = prompt("Respuesta:");
    if (a == null) return;
    const order = (items[items.length - 1]?.order || 0) + 1;
    await addDoc(collection(db, "faq_categories", catId, "items"), { q, a, order, active: true });
  }
  async function saveItem(it) {
    await setDoc(doc(db, "faq_categories", catId, "items", it.id), it, { merge: true });
  }
  async function deleteItem(it) {
    if (!window.confirm("¿Eliminar esta pregunta?")) return;
    await deleteDoc(doc(db, "faq_categories", catId, "items", it.id));
  }

  const selectedCat = useMemo(() => cats.find(c => c.id === catId), [cats, catId]);

  // --------- UI helpers (solo clases) ----------
  const card = "bg-white border border-slate-200/70 rounded-2xl shadow-sm";
  const subCard = "bg-slate-50 border border-slate-200/70 rounded-2xl";
  const field =
    "w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-sm " +
    "placeholder:text-slate-400 " +
    "hover:border-slate-400 " +
    "focus:outline-none focus:ring-4 focus:ring-blue-500/15 focus:border-blue-500";
  const btnPrimary =
    "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold " +
    "bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 " +
    "shadow-sm";
  const btnSecondary =
    "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold " +
    "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50";
  const btnDanger =
    "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold " +
    "border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100";

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-900">FAQ · Administrador</h1>
        <p className="text-sm text-slate-600">Gestiona categorías y preguntas visibles para usuarios.</p>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        {/* Sidebar categorías */}
        <aside className={`${card} p-4`}>
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <div className="text-sm font-bold text-slate-900">Categorías</div>
              <div className="text-xs text-slate-500">Organiza las preguntas por tema</div>
            </div>

            <button onClick={addCategory} className={btnPrimary}>
              <Plus size={16} />
              Nueva
            </button>
          </div>

          <div className="space-y-2">
            {cats.map((c) => {
              const active = catId === c.id;
              return (
                <div
                  key={c.id}
                  className={
                    "group rounded-2xl border p-3 transition " +
                    (active
                      ? "border-blue-200 bg-blue-50"
                      : "border-slate-200/70 bg-white hover:bg-slate-50")
                  }
                >
                  <button
                    onClick={() => setCatId(c.id)}
                    className="w-full text-left"
                  >
                    <div className="font-semibold text-slate-900">{c.title}</div>
                    <div className="text-xs text-slate-500">Orden: {c.order ?? 0}</div>
                  </button>

                  <div className="mt-3 flex items-center gap-2">
                    <button onClick={() => renameCategory(c)} className={btnSecondary}>
                      Renombrar
                    </button>
                    <button onClick={() => deleteCategory(c)} className={btnDanger}>
                      Borrar
                    </button>
                  </div>
                </div>
              );
            })}

            {cats.length === 0 && (
              <div className={`${subCard} p-4 text-sm text-slate-600`}>
                No hay categorías aún. Crea la primera con <b>Nueva</b>.
              </div>
            )}
          </div>
        </aside>

        {/* Items */}
        <main className={`${card} p-4`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div>
              <div className="text-sm font-bold text-slate-900">
                Preguntas {selectedCat?.title ? `· ${selectedCat.title}` : ""}{" "}
                <span className="text-slate-500 font-semibold">({items.length})</span>
              </div>
              <div className="text-xs text-slate-500">
                Edita la pregunta y respuesta. Usa “Activo” para mostrar/ocultar.
              </div>
            </div>

            <button onClick={addItem} className={btnPrimary}>
              <Plus size={16} />
              Agregar pregunta
            </button>
          </div>

          <div className="space-y-4">
            {items.map((it, idx) => (
              <div key={it.id} className="rounded-2xl border border-slate-200/70 bg-white shadow-sm overflow-hidden">
                {/* Header del item */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200/70">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-bold text-slate-900">
                      Pregunta #{idx + 1}
                    </div>

                    <span
                      className={
                        "text-xs font-semibold px-2 py-0.5 rounded-full border " +
                        (it.active !== false
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-100 text-slate-600 border-slate-200")
                      }
                    >
                      {it.active !== false ? "Activa" : "Oculta"}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={() => saveItem(items[idx])} className={btnPrimary}>
                      <Save size={16} />
                      Guardar
                    </button>
                    <button onClick={() => deleteItem(it)} className={btnDanger}>
                      <Trash2 size={16} />
                      Eliminar
                    </button>
                  </div>
                </div>

                {/* Body */}
                <div className="p-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-700">Pregunta</label>
                      <input
                        className={field}
                        value={it.q || ""}
                        onChange={(e) => {
                          const newItems = [...items];
                          newItems[idx] = { ...newItems[idx], q: e.target.value };
                          setItems(newItems);
                        }}
                        placeholder="Escribe la pregunta…"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-700">Orden</label>
                      <input
                        type="number"
                        className={field}
                        value={it.order ?? 0}
                        onChange={(e) => {
                          const newItems = [...items];
                          newItems[idx] = { ...newItems[idx], order: Number(e.target.value) };
                          setItems(newItems);
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    <label className="text-xs font-bold text-slate-700">Respuesta</label>
                    <textarea
                      className={`${field} min-h-[120px]`}
                      value={it.a || ""}
                      onChange={(e) => {
                        const newItems = [...items];
                        newItems[idx] = { ...newItems[idx], a: e.target.value };
                        setItems(newItems);
                      }}
                      placeholder="Escribe la respuesta…"
                    />
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-blue-600"
                        checked={it.active !== false}
                        onChange={(e) => {
                          const newItems = [...items];
                          newItems[idx] = { ...newItems[idx], active: e.target.checked };
                          setItems(newItems);
                        }}
                      />
                      Activo (visible)
                    </label>
                  </div>
                </div>
              </div>
            ))}

            {items.length === 0 && (
              <div className={`${subCard} p-5 text-sm text-slate-600`}>
                No hay preguntas en esta categoría. Crea una con <b>Agregar pregunta</b>.
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
